import { apiService, ApiError } from '../services/apiService';
import { AlertConfiguration, AlertDashboardNotice } from '../types/dashboard';
import {
  formatDashboardNoticeMessage,
  formatRecentAlertMessage,
  MISSING_TRIGGER_WEBHOOK_MESSAGE,
} from './alertUserFacingText';

function parseAlertId(config: AlertConfiguration): number | null {
  const raw = config.id;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return parseInt(raw, 10);
  return null;
}

function label(config: AlertConfiguration): { stockSymbol: string; positionType: string } {
  return {
    stockSymbol: config.stock_symbol ?? config.stockSymbol ?? 'Alert',
    positionType: config.position_type ?? config.positionType ?? '',
  };
}

function webhookSlotIndices(config: AlertConfiguration): number[] {
  const sources = config.slot_sources ?? [];
  return sources
    .map((s, i) => (String(s).toLowerCase() === 'webhook' ? i : -1))
    .filter((i) => i >= 0);
}

function slotTokenMissingMessage(_slotIndex: number, apiMessage?: string): string {
  return formatDashboardNoticeMessage(apiMessage) || MISSING_TRIGGER_WEBHOOK_MESSAGE;
}

function alertTokenMissingMessage(apiMessage?: string): string {
  return formatDashboardNoticeMessage(apiMessage);
}

function outboundWebhookMissingMessage(): string {
  return 'No outbound webhook URL on this alert. Open Alerts, edit this alert, and set the webhook URL that runs when the alert fires.';
}

function errorNotice(
  alertId: number,
  config: AlertConfiguration,
  message: string
): AlertDashboardNotice {
  const { stockSymbol, positionType } = label(config);
  return { alertId, stockSymbol, positionType, severity: 'error', message };
}

function pushNotice(
  notices: AlertDashboardNotice[],
  config: AlertConfiguration,
  alertId: number,
  message: string,
  severity: AlertDashboardNotice['severity'] = 'warning'
): void {
  const { stockSymbol, positionType } = label(config);
  notices.push({ alertId, stockSymbol, positionType, severity, message });
}

/** Load per-alert webhook / incoming-trigger setup issues for the dashboard. */
export async function buildIncomingWebhookNotices(
  configs: AlertConfiguration[],
  authToken?: string
): Promise<AlertDashboardNotice[]> {
  const notices: AlertDashboardNotice[] = [];
  const active = configs.filter((c) => c.is_active ?? true);

  for (const config of active) {
    const alertId = parseAlertId(config);
    if (!alertId) continue;

    const webhookSlots = webhookSlotIndices(config);
    const incomingToken = (config.incoming_webhook_token ?? '').trim();

    // Single-trigger: outbound execution URL
    if (!config.is_multi_trigger) {
      const outbound = (config.webhook_url ?? config.webhookUrl ?? '').trim();
      if (!outbound) {
        pushNotice(notices, config, alertId, outboundWebhookMissingMessage());
      }
    }

    // Per-slot incoming tokens (multi-trigger with webhook slots)
    if (config.is_multi_trigger && webhookSlots.length > 0) {
      for (const slotIndex of webhookSlots) {
        try {
          const res = await apiService.getAlertSlotTriggerToken(alertId, slotIndex, authToken);
          const hasToken =
            !!res.data?.token || !!res.data?.tokenUp || !!res.data?.tokenDown;
          if (!hasToken) {
            pushNotice(notices, config, alertId, slotTokenMissingMessage(slotIndex, res.message));
          }
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            continue;
          }
          const msg =
            e instanceof ApiError
              ? formatRecentAlertMessage(apiService.formatErrorDetail(e) ?? e.message) ??
                'Could not check incoming webhook URL for this slot.'
              : 'Could not check incoming webhook URL for this slot.';
          notices.push(errorNotice(alertId, config, msg));
        }
      }
    }

    // Alert-level incoming trigger (single-trigger, or multi with email-only slots)
    const needsAlertLevelIncoming =
      !config.is_multi_trigger || webhookSlots.length === 0;
    if (needsAlertLevelIncoming && !incomingToken) {
      try {
        const res = await apiService.getAlertTriggerToken(alertId, authToken);
        if (!res.data?.token) {
          pushNotice(notices, config, alertId, alertTokenMissingMessage(res.message));
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          continue;
        }
        if (e instanceof ApiError && e.status === 404) {
          pushNotice(
            notices,
            config,
            alertId,
            alertTokenMissingMessage(apiService.formatErrorDetail(e) ?? undefined)
          );
        } else {
          const msg =
            e instanceof ApiError
              ? formatRecentAlertMessage(apiService.formatErrorDetail(e) ?? e.message) ??
                'Could not check incoming webhook URL for this alert.'
              : 'Could not check incoming webhook URL for this alert.';
          notices.push(errorNotice(alertId, config, msg));
        }
      }
    }
  }

  return notices;
}

export function mergeNoticesIntoSlotRows<T extends { alertId: number }>(
  rows: T[],
  notices: AlertDashboardNotice[]
): (T & { incomingWebhookNotice?: string; incomingWebhookSeverity?: AlertDashboardNotice['severity'] })[] {
  const byAlert = new Map<number, AlertDashboardNotice[]>();
  for (const n of notices) {
    const list = byAlert.get(n.alertId) ?? [];
    list.push(n);
    byAlert.set(n.alertId, list);
  }

  return rows.map((row) => {
    const list = byAlert.get(row.alertId);
    if (!list?.length) return row;
    const severity = list.some((n) => n.severity === 'error') ? 'error' : 'warning';
    return {
      ...row,
      incomingWebhookNotice: list.map((n) => n.message).join(' '),
      incomingWebhookSeverity: severity,
    };
  });
}
